import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { getDatabase, ref, push } from 'firebase/database';
import bgImage from '../assets/homebg1005.jpg';
import Footer from './Footer';
import DomesticTours from './DomesticTours';
import InternationalTours from './InternationalTours';
import AboutUs from './AboutUs';
import Gallery from './Gallery';
import Testimonials from './Testimonials';
import ContactUs from './ContactUs';
import Navbar from './Navbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

const Home = () => {
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [showNumber, setShowNumber] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    tourType: 'general'
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowEnquiry(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Validation patterns
  const validators = {
    name: {
      pattern: /^[A-Za-z\s]{2,50}$/,
      message: 'Name must be 2-50 characters long and contain only letters and spaces'
    },
    email: {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address'
    },
    phone: {
      pattern: /^\+?\d{10,14}$/,
      message: 'Phone number must be 10-14 digits (can include country code with +)'
    },
    message: {
      pattern: /^.{0,500}$/,
      message: 'Message must be 0-500 characters long'
    }
  };

  const toggleNumber = () => setShowNumber(!showNumber);

  const openWhatsApp = () => {
    const phoneNumber = '919697988891';
    const message = 'Hello! I am interested in your tour packages. Please provide more information.';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    const validator = validators[name];
    if (!validator) return;

    if (name !== 'message' && !value.trim()) {
      setErrors(prev => ({ ...prev, [name]: 'This field is required' }));
    } else if (!validator.pattern.test(value)) {
      setErrors(prev => ({ ...prev, [name]: validator.message }));
    } else {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    Object.keys(formData).forEach(key => {
      if (key === 'tourType') return;
      const validator = validators[key];
      if (!validator) return;

      if (key !== 'message' && !formData[key].trim()) {
        newErrors[key] = 'This field is required';
        isValid = false;
      } else if (!validator.pattern.test(formData[key])) {
        newErrors[key] = validator.message;
        isValid = false;
      } else {
        newErrors[key] = '';
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    const timestamp = new Date().toISOString();
    const enquiryData = { ...formData, timestamp, status: 'unread' };
    const db = getDatabase();
    const enquiriesRef = ref(db, 'enquiries');
    push(enquiriesRef, enquiryData)
      .then(() => {
        setSubmitSuccess(true);
        setShowEnquiry(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          tourType: 'general'
        });
        setErrors({ name: '', email: '', phone: '', message: '' });
        setIsSubmitting(false);
        setTimeout(() => setSubmitSuccess(false), 5000);
      })
      .catch((error) => {
        console.error('Error saving enquiry:', error);
        setSubmitError('There was an error submitting your enquiry. Please try again.');
        setIsSubmitting(false);
      });
  };

  const closeEnquiry = () => setShowEnquiry(false);

  return (
    <>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; font-family: 'Poppins', sans-serif; }
        html { scroll-behavior: smooth; }
        body { overflow-x: hidden; background-color: #f0f4f8; }
        .hero-section {
          position: relative;
          height: 100vh;
          min-height: 600px;
          background: url(${bgImage}) center center/cover no-repeat;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          text-align: center;
          animation: fadeIn 1s ease;
        }
        .hero-section::before {
          content: '';
          position: absolute;
          top: 0; left: 0; width: 100%; height: 100%;
          // background: linear-gradient(rgba(33, 147, 176, 0.7), rgba(33, 147, 176, 0.3));
          z-index: 1;
        }
        .hero-content {
          position: relative;
          z-index: 2;
          max-width: 900px;
          padding: 0 20px;
        }
        .hero-heading {
          font-size: clamp(2.5rem, 6vw, 4.5rem);
          font-weight: 800;
          margin-bottom: 1rem;
          text-shadow: 2px 2px 6px rgba(0,0,0,0.5);
          animation: slideUp 0.8s ease;
        }
        .hero-heading1 {
          font-size: clamp(1.4rem, 4vw, 2.6rem);
          font-weight: 800;
          text-transform: uppercase;
          margin-bottom: 0.7rem;
          text-shadow: 2px 2px 7px rgba(0,0,0,0.6);
          letter-spacing: 1.5px;
        }
        .hero-subtext {
          font-size: clamp(1.2rem, 2.5vw, 1.8rem);
          font-weight: 400;
          margin-bottom: 1.5rem;
          animation: slideUp 1s ease;
        }
        .cta-buttons {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        .primary-btn, .secondary-btn {
          padding: 1.2rem 3rem;
          border-radius: 50px;
          font-size: 1.2rem;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          animation: bounceIn 1s ease;
          border: none;
          display: inline-block;
        }
        .primary-btn { background: #2193b0; color: #fff; }
        .primary-btn:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 15px rgba(33,147,176,0.3);
          background: #1a8ba5;
        }
        .secondary-btn { background: transparent; color: #fff; border: 2px solid #fff; }
        .secondary-btn:hover {
          background: #fff;
          color: #ff6b6b;
          transform: translateY(-4px);
          box-shadow: 0 6px 15px rgba(255,107,107,0.2);
        }
        .section {
          padding: 100px 5%;
          position: relative;
          animation: fadeIn 1s ease;
        }
        .section:nth-child(odd) { background: #fff; }
        .section:nth-child(even) { background: #f0f4f8; }
        .section-title {
          font-size: clamp(2rem, 4vw, 3rem);
          margin-bottom: 60px;
          text-align: center;
          color: #212121;
          font-weight: 700;
          position: relative;
          animation: slideIn 1s ease;
        }
        .section-title::after {
          content: '';
          position: absolute;
          bottom: -20px;
          left: 50%;
          transform: translateX(-50%);
          width: 100px;
          height: 5px;
          background: #ff6b6b;
          border-radius: 5px;
        }
        .enquiry-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.6);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s;
        }
        .enquiry-overlay.active { opacity: 1; visibility: visible; }
        .enquiry-form-container {
          background: #fff;
          border-radius: 15px;
          width: 90%;
          max-width: 450px;
          padding: 30px;
          box-shadow: 0 15px 30px rgba(0,0,0,0.2);
          transform: scale(0.9);
          transition: all 0.4s;
          animation: popIn 0.5s ease forwards;
        }
        .enquiry-overlay.active .enquiry-form-container { transform: scale(1); }
        .enquiry-form-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .enquiry-form-title { font-size: 1.6rem; color: #212121; }
        .close-enquiry-btn {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #2193b0;
          transition: color 0.2s;
        }
        .close-enquiry-btn:hover { color: #ff6b6b; }
        .enquiry-form { display: flex; flex-direction: column; gap: 15px; }
        .form-group { display: flex; flex-direction: column; }
        .form-label { margin-bottom: 5px; font-weight: 600; color: #212121; }
        .form-input, .form-select, .form-textarea {
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.3s;
        }
        .form-input:focus, .form-select:focus, .form-textarea:focus {
          border-color: #2193b0;
          outline: none;
          box-shadow: 0 0 5px rgba(33,147,176,0.5);
        }
        .form-input.error, .form-textarea.error {
          border-color: #e74c3c;
        }
        .form-textarea { min-height: 80px; resize: vertical; }
        .submit-btn {
          background: #2193b0;
          color: #fff;
          border: none;
          padding: 12px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          animation: bounceIn 0.8s ease;
        }
        .submit-btn:disabled {
          background: #cccccc;
          cursor: not-allowed;
          box-shadow: none;
        }
        .submit-btn:hover:not(:disabled) {
          background: #1a8ba5;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(33,147,176,0.3);
        }
        .error-message {
          color: #e74c3c;
          font-size: 0.85rem;
          margin-top: 5px;
          animation: fadeIn 0.3s ease;
        }
        .success-message {
          background: #2ecc71;
          color: white;
          padding: 15px;
          border-radius: 8px;
          text-align: center;
          margin-top: 20px;
          animation: fadeIn 0.5s ease;
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          width: 90%;
          max-width: 400px;
          z-index: 1001;
        }
        .submit-error {
          background: #e74c3c;
          color: white;
          padding: 15px;
          border-radius: 8px;
          text-align: center;
          margin-top: 20px;
          animation: fadeIn 0.5s ease;
        }
        .floating-whatsapp-container {
          position: fixed;
          right: 20px;
          bottom: 20px;
          z-index: 999;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }
        .whatsapp-button {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: #25D366;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          transition: all 0.3s;
          animation: pulse 2s infinite;
          margin-bottom: 10px;
        }
        .whatsapp-button:hover {
          transform: scale(1.1);
          background: #1da851;
        }
        .whatsapp-button .icon { font-size: 30px;}
        .whatsapp-number-display {
          background: #25D366;
          color: #fff;
          padding: 10px 15px;
          border-radius: 20px;
          font-weight: 500;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          opacity: ${showNumber ? '1' : '0'};
          transform: translateY(${showNumber ? '0' : '10px'});
          transition: all 0.3s;
          margin-bottom: 10px;
          text-align: center;
          font-size: 14px;
          max-width: 200px;
          animation: fadeIn 0.5s ease;
        }
        /* Keyframes */
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; }}
        @keyframes slideIn { from { transform: translateX(-20px); opacity: 0; } to { transform: translateX(0); opacity: 1; }}
        @keyframes bounceIn { 0% { transform: scale(0.9); opacity: 0; } 50% { transform: scale(1.05); opacity: 0.5; } 100% { transform: scale(1); opacity: 1; }}
        @keyframes popIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; }}
        @keyframes pulse { 0% { transform: scale(1); } 50% { transform: scale(1.05); } 100% { transform: scale(1); } }
        /* Responsive */
        @media (max-width: 1200px) { .hero-content { max-width: 800px; } .section { padding: 90px 4%; } }
        @media (max-width: 992px) {
          .hero-heading { font-size: clamp(2.2rem, 5.5vw, 3.8rem);}
          .hero-heading1 { font-size: clamp(1.3rem, 3.5vw, 2.4rem);}
          .hero-subtext { font-size: clamp(1.1rem, 2.2vw, 1.6rem);}
          .cta-buttons { gap: 1.2rem;}
          .primary-btn, .secondary-btn {padding: 1rem 2rem;font-size: 1.1rem;max-width: 250px;}
          .section-title { font-size: clamp(1.8rem, 3.8vw, 2.8rem);}
        }
        @media (max-width: 768px) {
          .cta-buttons { flex-direction: column; gap: 1.5rem; align-items: center;}
          .primary-btn, .secondary-btn { width: 100%; max-width: 280px; padding: 1rem 1.5rem; font-size: 1.2rem;}
          .section { padding: 80px 4%;}
          .hero-section { min-height: 550px;}
          .hero-content { padding: 0 15px;}
          .hero-heading1 { font-size: clamp(1.2rem, 3.5vw, 2.2rem); margin-bottom: 0.5rem;}
          .hero-heading { font-size: clamp(2rem, 5.5vw, 3.5rem); margin-bottom: 0.8rem;}
          .hero-subtext { font-size: clamp(1rem, 2.2vw, 1.4rem); margin-bottom: 1rem;}
          .floating-whatsapp-container { right: 15px; bottom: 15px;}
          .whatsapp-button { width: 50px; height: 50px; margin-bottom: 8px;}
          .whatsapp-button .icon { font-size: 24px;}
          .whatsapp-number-display { font-size: 12px; padding: 8px 12px; max-width: 180px;}
          .enquiry-form-container { max-width: 400px; padding: 25px; }
        }
        @media (max-width: 576px) {
          .hero-section { min-height: 450px;}
          .hero-content { padding: 0 10px;}
          .hero-heading { font-size: clamp(1.8rem, 7vw, 3rem); margin-bottom: 0.6rem;}
          .hero-heading1 { font-size: clamp(1rem, 4vw, 1.8rem); margin-bottom: 0.4rem;}
          .hero-subtext { font-size: clamp(0.9rem, 3vw, 1.2rem); margin-bottom: 0.8rem;}
          .cta-buttons { gap: 1rem;}
          .primary-btn, .secondary-btn {max-width: 250px; padding: 0.9rem 1rem; font-size: 1.1rem;}
          .section { padding: 70px 3%;}
          .section-title { font-size: clamp(1.6rem, 5vw, 2.5rem); margin-bottom: 50px;}
          .section-title::after { width: 80px; height: 4px; bottom: -15px;}
          .enquiry-form-container { max-width: 350px; padding: 20px; }
          .enquiry-form-title { font-size: 1.5rem;}
          .form-input, .form-select, .form-textarea { font-size: 0.95rem;}
          .submit-btn { padding: 10px; font-size: 0.95rem;}
          .error-message { font-size: 0.8rem;}
        }
        @media (max-width: 400px) {
          .hero-heading { font-size: clamp(1.6rem, 8vw, 2.8rem); margin-bottom: 0.5rem;}
          .hero-heading1 { font-size: clamp(0.9rem, 4.5vw, 1.6rem); margin-bottom: 0.3rem;}
          .hero-subtext { font-size: clamp(0.8rem, 3.5vw, 1.1rem); margin-bottom: 0.6rem;}
          .primary-btn, .secondary-btn {font-size: 1rem; padding: 0.8rem; max-width: 200px;}
          .floating-whatsapp-container { right: 8px; bottom: 8px;}
          .whatsapp-button { width: 40px; height: 40px; margin-bottom: 5px;}
          .whatsapp-button .icon { font-size: 18px;}
          .whatsapp-number-display { font-size: 9px; padding: 5px 8px; max-width: 130px;}
          .enquiry-form-container { max-width: 300px; padding: 15px;}
          .enquiry-form-title { font-size: 1.4rem;}
          .form-label { font-size: 0.9rem;}
        }
        sup { font-size: 3rem; }
      `}</style>

      <Navbar />

      {/* Floating WhatsApp Button */}
      <div className="floating-whatsapp-container">
        {showNumber && <div className="whatsapp-number-display">+91 9697988891</div>}
        <button
          className="whatsapp-button"
          onClick={openWhatsApp}
          onMouseEnter={toggleNumber}
          onMouseLeave={toggleNumber}
          title="Chat with us on WhatsApp"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="icon" />
        </button>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-heading">NMT INDIA HOLIDAYS<sup>®</sup></h1>
          <h2 className="hero-heading1">your</h2>
          <h2 className="hero-heading1">Perfect Travelling Partner</h2>
          <div className="cta-buttons">
            <Link to="/domestic-tours" className="primary-btn">Explore Tours</Link>
            <Link to="/contact-us" className="secondary-btn">Contact Us</Link>
          </div>
        </div>
      </section>

      {/* Domestic Tours Section */}
      <section className="section">
        <h2 className="section-title">Domestic Tour Packages</h2>
        <DomesticTours />
      </section>

      {/* International Tours Section */}
      <section className="section">
        <h2 className="section-title">International Destinations</h2>
        <InternationalTours />
      </section>

      {/* About Us Section */}
      <section className="section">
        <AboutUs />
      </section>

      {/* Gallery Section */}
      <section className="section">
        <Gallery />
      </section>

      {/* Testimonials Section */}
      <section className="section">
        <Testimonials />
      </section>

      {/* Contact Us Section */}
      <section className="section">
        <ContactUs />
      </section>

      {/* Enquiry popup */}
      {showEnquiry && (
        <div className="enquiry-overlay active" onClick={closeEnquiry}>
          <div className="enquiry-form-container" onClick={e => e.stopPropagation()}>
            <div className="enquiry-form-header">
              <h3 className="enquiry-form-title">Quick Enquiry</h3>
              <button className="close-enquiry-btn" onClick={closeEnquiry}>×</button>
            </div>
            <form className="enquiry-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input 
                  type="text" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleInputChange} 
                  className={`form-input ${errors.name ? 'error' : ''}`} 
                  required 
                />
                {errors.name && <div className="error-message">{errors.name}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleInputChange} 
                  className={`form-input ${errors.email ? 'error' : ''}`} 
                  required 
                />
                {errors.email && <div className="error-message">{errors.email}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input 
                  type="tel" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleInputChange} 
                  className={`form-input ${errors.phone ? 'error' : ''}`} 
                  required 
                />
                {errors.phone && <div className="error-message">{errors.phone}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea 
                  name="message" 
                  value={formData.message} 
                  onChange={handleInputChange} 
                  className={`form-textarea ${errors.message ? 'error' : ''}`}
                ></textarea>
                {errors.message && <div className="error-message">{errors.message}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Tour Type</label>
                <select 
                  name="tourType" 
                  value={formData.tourType} 
                  onChange={handleInputChange} 
                  className="form-select"
                >
                  <option value="general">General</option>
                  <option value="domestic">Domestic</option>
                  <option value="international">International</option>
                </select>
              </div>
              <button 
                type="submit" 
                className="submit-btn" 
                disabled={isSubmitting || Object.values(errors).some(error => error)}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
              {submitError && <div className="submit-error">{submitError}</div>}
            </form>
          </div>
        </div>
      )}

      {submitSuccess && (
        <div className="success-message">
          Thank you {formData.name}! We'll contact you soon.
        </div>
      )}

      <Footer />
    </>
  );
};

export default Home;  